import { cn } from '@rocco/utils/cn';
import { BLEED_GUTTER_X } from '~/ui-rocco/pages/detail-shared';

interface EditorialSectionHeaderProps {
    /**
     * Rendered verbatim, brackets included. The directory's headers are
     * bracketed in the design — `(Featured Article)` — and the category page's
     * is not, so the caller owns the exact string.
     */
    title: string;
    /**
     * `sm` is the directory's 33/-0.825, `lg` the category page's 40/-1. Same
     * component in the design, two type sizes.
     */
    size?: 'sm' | 'lg';
    /** Set where a section names itself by this heading. */
    id?: string;
    className?: string;
}

/**
 * The rule-and-title that opens each editorial section.
 *
 * Figma 60:2063 (`Frame 1321321884`, 84 tall on white): a hairline across the
 * 1824 content column 7px down, with a Gestura 33/1.25 title beneath it. The
 * rule is black at 50% opacity in the design, not a grey — it reads lighter
 * over white but keeps its weight on any ground.
 */
export const EditorialSectionHeader = ({
    title,
    size = 'sm',
    id,
    className,
}: EditorialSectionHeaderProps) => {
    return (
        <div
            className={cn(
                'relative h-[5.25rem] px-[1.5rem]',
                BLEED_GUTTER_X,
                className,
            )}
        >
            <div className="relative h-full">
                <div
                    aria-hidden={true}
                    className="absolute inset-x-0 top-[0.4375rem] h-px bg-black/50"
                />
                <h2
                    id={id}
                    className={cn(
                        'absolute top-[1.25rem] left-0 m-0 font-gestura leading-[1.25] font-normal text-[#181818]',
                        size === 'lg'
                            ? 'text-[2.5rem] tracking-[-0.0625rem]'
                            : 'text-[2.0625rem] tracking-[-0.0516rem]',
                    )}
                >
                    {title}
                </h2>
            </div>
        </div>
    );
};
